import { useQuery } from '@apollo/react-hooks';
import { UAContext } from '@quentin-sommer/react-useragent';
import { NextPage } from 'next';
import React, { useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Layout from '~components/layout';
import {
  BookingCard,
  BookingProgressCard,
  BookingReminder,
  MyBookingsLoader,
  NoBooked,
} from '~components/my-bookings';
import Typography from '~components/typography';
import {
  BookingLinkStatusTypeOption,
  BookingStatusOption,
  OfferStatusOption,
} from '~graphql/__generated__/globalTypes';
import {
  SP_QueryStudentGetBookings,
  SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings,
} from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { QUERY_STUDENT_GET_BOOKINGS } from '~graphql/user';
import { useTranslation } from '~i18n';
import styles from '~public/styles/my-bookings.module.scss';
import { updateViewerInfo } from '~store/user/actions';
import {
  BookingPendingOrderStatus,
  BookingProcesingOrderStatus,
  BookingStatusCanceledAndPast,
  BookingStatusProcesing,
  BookingStatusProcesingWithEnquiry,
  PaymentItemPaidStatus,
} from '~utils/use-status';
import useTrackingCountry from '~utils/use-tracking-country';

const MyBookings: NextPage = () => {
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data, refetch, loading } = useQuery<SP_QueryStudentGetBookings>(
    QUERY_STUDENT_GET_BOOKINGS,
    {
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        if (data?.studentViewer) {
          let viewer = JSON.parse(JSON.stringify(data?.studentViewer));
          //@ts-ignore
          delete viewer?.studentInfo?.bookings;
          dispatch(updateViewerInfo(viewer));
        }
      },
    }
  );
  const bookings = data?.studentViewer?.studentInfo?.bookings;
  const contact = data?.studentViewer?.studentInfo?.contact;

  useTrackingCountry(bookings);

  const bookingsArchived = useMemo(() => {
    let archived: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings[] = [];
    bookings?.forEach(
      (item: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings | null) => {
        if (!item?.status) return;
        if (item.status === BookingStatusOption.EXPIRED && !item?.sourceBooking) {
          archived.push(item);
        }
        if (BookingStatusCanceledAndPast.includes(item.status)) {
          const isUnPaid = item?.paymentItems.some((item: any) => {
            return !PaymentItemPaidStatus.includes(item.status);
          });
          if (!isUnPaid) archived.push(item);
        }
      }
    );
    return archived;
  }, [bookings]);

  const bookingInProgress = useMemo(() => {
    if (!bookings) return [];
    const progress = bookings.filter(
      (booking): booking is SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings => {
        if (!booking) return false;
        const { status, sourceBooking, offerStatus, bookingLink } = booking;
        if (
          status &&
          BookingStatusProcesing.includes(status) &&
          !sourceBooking &&
          bookingLink?.status !== BookingLinkStatusTypeOption.PENDING
        ) {
          return true;
        }
        if (status === BookingStatusOption.ENQUIRY && offerStatus === OfferStatusOption.OFFERED) {
          return true;
        }
        return false;
      }
    );
    progress.sort((pre: any, next: any) => {
      return (
        BookingProcesingOrderStatus.indexOf(pre.status) -
        BookingProcesingOrderStatus.indexOf(next.status)
      );
    });

    return progress;
  }, [bookings]);

  const bookingsPending = useMemo(() => {
    const current = (bookings ?? [])
      .filter(
        (booking): booking is SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings =>
          !!booking
      )
      .filter(booking => {
        switch (booking.status) {
          case BookingStatusOption.BOOKING_COMPLETED:
          case BookingStatusOption.BOOKING_CONFIRMED:
            if (
              booking?.bookingLink &&
              booking?.bookingLink?.status === BookingLinkStatusTypeOption.PENDING &&
              booking?.id === booking.bookingLink?.replacementBooking?.id &&
              booking?.bookingLink?.sourceAcceptedAt
            ) {
              return false;
            }
            return true;
          case BookingStatusOption.RESIDENT: {
            return true;
          }
          case BookingStatusOption.ROOM_SELECTED:
          case BookingStatusOption.SELECTION_COMPLETED:
          case BookingStatusOption.DETAILS_COMPLETED:
          case BookingStatusOption.TERMS_ACCEPTED: {
            if (
              booking?.bookingLink &&
              booking?.bookingLink?.status === BookingLinkStatusTypeOption.PENDING &&
              booking?.id === booking?.bookingLink?.occupiedBooking?.id
            ) {
              return true;
            }
            return false;
          }

          case BookingStatusOption.PAST:
          case BookingStatusOption.CANCELLED: {
            return booking.paymentItems.some((item: any) => {
              return !PaymentItemPaidStatus.includes(item?.status);
            });
          }
          default: {
            return false;
          }
        }
      });

    current.sort((pre: any, next: any) => {
      return (
        new Date(pre.tenancyOption.startDate).getTime() -
        new Date(next.tenancyOption.startDate).getTime()
      );
    });

    return current.sort((pre, next) => {
      if (!pre.status || !next.status) return 0;
      return (
        BookingPendingOrderStatus.indexOf(pre.status) -
        BookingPendingOrderStatus.indexOf(next.status)
      );
    });
  }, [bookings]);

  const renderMyBookingsNoBooked = useCallback(() => {
    if (
      bookingsPending?.length === 0 &&
      bookingInProgress?.length === 0 &&
      bookingsArchived?.length === 0 &&
      !loading
    ) {
      return <NoBooked />;
    }
  }, [bookingInProgress, bookingsArchived, bookingsPending, loading]);

  const renderReminderBookings = useCallback(() => {
    let progress: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings[] = [];
    bookings?.forEach(item => {
      if (!item || !item?.status) return;
      if (BookingStatusProcesingWithEnquiry.includes(item.status)) progress.push(item);
    });
    const heldBooking = progress.filter((item: any) => {
      return item.heldToTime;
    });

    const pendingBooking = progress.filter((item: any) => {
      return !item.heldToTime && item.countDown;
    });
    heldBooking.sort((pre: any, next: any) => {
      return new Date(pre.heldToTime).getTime() - new Date(next.heldToTime).getTime();
    });

    pendingBooking.sort((pre: any, next: any) => {
      return new Date(pre.countDown).getTime() - new Date(next.countDown).getTime();
    });

    if (!loading && (heldBooking.length > 0 || pendingBooking.length > 0)) {
      return (
        <BookingReminder
          bookings={{
            heldBooking,
            pendingBooking,
          }}
        />
      );
    }
  }, [bookings, loading]);

  const renderSectionTitle = (title: string) => {
    return (
      <div className={styles.pending}>
        <Typography
          component="div"
          variant={isMobile ? 'h8' : 'h4'}
          className={styles.sectionTitle}
        >
          {title}
        </Typography>
      </div>
    );
  };

  const renderMyBookingsLoading = () => loading && <MyBookingsLoader />;

  return (
    <Layout layoutFlexible={true} className={styles.layout}>
      <div className={styles.root}>
        <div className={styles.pending}>
          <Typography component="div" variant={isMobile ? 'h7' : 'h2'}>
            {t('my_bookings')}
          </Typography>

          {renderReminderBookings()}
          {renderMyBookingsLoading()}
          {renderMyBookingsNoBooked()}
        </div>
        {bookingsPending.length > 0 && (
          <div className={styles.section}>
            {renderSectionTitle(t('bookings.current'))}
            {bookingsPending.map((item, index) => (
              <BookingCard key={`booking${index}`} booking={item} contact={contact} />
            ))}
          </div>
        )}

        {bookingInProgress.length > 0 && (
          <div className={styles.section} id="booking-progress">
            {renderSectionTitle(t('bookings.bookings_in_progress'))}
            <div className={styles.cardWrapper}>
              {bookingInProgress.map((item, index) => {
                return (
                  <BookingProgressCard
                    key={`progress${index}`}
                    className={styles.card}
                    booking={item}
                    onDelete={() => refetch()}
                  />
                );
              })}
            </div>
          </div>
        )}
        {bookingsArchived.length > 0 && (
          <div className={styles.section}>
            {renderSectionTitle(t('bookings.past_archived'))}
            <div className={styles.cardWrapper}>
              {bookingsArchived.map((item, index) => {
                return (
                  <BookingProgressCard
                    key={`archived${index}`}
                    booking={item}
                    className={styles.card}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
MyBookings.getInitialProps = async () => ({
  namespacesRequired: ['public', 'student_portal'],
});

export default MyBookings;
